import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";
import { Landmark, Check } from "lucide-react";

const COOP_TYPES = ["Koperasi Kakitangan", "Koperasi Sekolah / Universiti", "Koperasi Komuniti", "Koperasi Perumahan", "Koperasi Perladangan", "Koperasi Retail", "Koperasi Pengangkutan", "Koperasi Travel", "Koperasi Pembiayaan", "Lain-lain"];

const PLANS = [
  { code: "starter", name: "STARTER", monthly: 299, features: ["Ahli & KYC", "Saham & Yuran", "Bayaran & Resit", "Portal Ahli", "Laporan Asas"] },
  { code: "professional", name: "PROFESSIONAL", monthly: 799, popular: true, features: ["Semua Starter", "Pembiayaan", "Perakaunan", "AGM & E-Voting", "Pembantu AI"] },
  { code: "business", name: "BUSINESS", monthly: 2000, features: ["Semua Professional", "Perolehan", "Belanjawan", "Tadbir Urus & Risiko", "AI Lanjutan"] },
];

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [coopType, setCoopType] = useState(COOP_TYPES[0]);
  const [plan, setPlan] = useState("professional");
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { lang } = useLang();

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tenants/onboard", { name, coop_type: coopType, plan, cycle });
      toast.success(lang === "bm" ? "Koperasi berjaya didaftarkan! Percubaan 14 hari bermula." : "Cooperative registered! 14-day trial started.");
      window.location.href = "/admin";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ralat / Error");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-brand-deep flex items-center justify-center">
            <Landmark className="w-6 h-6 text-gold" />
          </div>
          <div>
            <div className="font-display font-bold text-brand-dark">Daftar Koperasi Anda</div>
            <div className="text-xs text-muted-foreground uppercase tracking-[0.15em]">Langkah {step} / 2 • Percubaan 14 hari</div>
          </div>
        </div>

        {step === 1 && (
          <div className="bg-card rounded-2xl border border-brand-deep/10 p-6 sm:p-8 animate-fade-up">
            <h2 className="font-display text-xl font-bold text-brand-dark mb-6">Maklumat Koperasi</h2>
            <label className="block text-sm font-medium mb-1.5">Nama Koperasi</label>
            <input
              data-testid="onboard-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="cth: Koperasi Kakitangan Maju Berhad"
              className="w-full rounded-lg border border-input px-4 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            />
            <label className="block text-sm font-medium mb-1.5">Jenis Koperasi</label>
            <select
              data-testid="onboard-type-select"
              value={coopType}
              onChange={(e) => setCoopType(e.target.value)}
              className="w-full rounded-lg border border-input px-4 py-2.5 text-sm mb-8 bg-white focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            >
              {COOP_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              data-testid="onboard-next-btn"
              disabled={name.trim().length < 3}
              onClick={() => setStep(2)}
              className="w-full bg-brand-deep text-white rounded-xl py-3 font-semibold hover:bg-brand-dark transition-colors disabled:opacity-40"
            >
              Seterusnya: Pilih Pelan
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <div className="flex justify-center gap-2 mb-6">
              {["monthly", "annual"].map((c) => (
                <button
                  key={c}
                  data-testid={`onboard-cycle-${c}`}
                  onClick={() => setCycle(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cycle === c ? "bg-brand-deep text-white" : "bg-white border border-brand-deep/20 text-brand-deep"}`}
                >
                  {c === "monthly" ? "Bulanan" : "Tahunan (jimatan)"}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {PLANS.map((p) => (
                <button
                  key={p.code}
                  data-testid={`onboard-plan-${p.code}`}
                  onClick={() => setPlan(p.code)}
                  className={`relative text-left rounded-2xl border-2 p-5 transition-all bg-card ${
                    plan === p.code ? "border-gold shadow-lg" : "border-brand-deep/10 hover:border-brand-deep/30"
                  }`}
                >
                  {p.popular && <span className="absolute -top-3 left-4 bg-gold text-brand-dark text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Popular</span>}
                  <div className="font-display font-bold text-brand-dark">{p.name}</div>
                  <div className="font-display text-2xl font-extrabold text-brand-deep mt-1">
                    RM{(cycle === "monthly" ? p.monthly : p.monthly * 10).toLocaleString()}
                    <span className="text-xs font-medium text-muted-foreground">/{cycle === "monthly" ? "bln" : "thn"}</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Check className="w-3 h-3 text-brand-vivid" /> {f}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button data-testid="onboard-back-btn" onClick={() => setStep(1)} className="px-6 py-3 rounded-xl border border-brand-deep/20 text-brand-deep font-semibold hover:bg-brand-light transition-colors">
                Kembali
              </button>
              <button
                data-testid="onboard-submit-btn"
                onClick={submit}
                disabled={busy}
                className="flex-1 bg-gold text-brand-dark rounded-xl py-3 font-bold hover:bg-gold-hover transition-colors disabled:opacity-50"
              >
                {busy ? "Memproses..." : "Mula Percubaan 14 Hari"}
              </button>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-4">Langganan boleh diaktifkan dengan Stripe bila-bila masa dari halaman Langganan.</p>
          </div>
        )}
      </div>
    </div>
  );
}
